/**
 * App Bar Component
 * Top navigation bar with global fuzzy search type-ahead
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  ClickAwayListener,
  Popper,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Fuse from 'fuse.js';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setDrawerNavOpen } from '../../store/slices/uiSlice';
import { setQuery } from '../../store/slices/searchSlice';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { colorPalette } from '../../theme';
import * as api from '../../services/api';
import MatracLogo from '../../assets/Matrac-by-assetrac-Logo.svg';
import ProjectPickerDialog from './ProjectPickerDialog';

const FUSE_OPTIONS = {
  keys: ['name', 'unitNumber', 'uuid', 'description', { name: 'typeName', weight: 0.5 }],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

/* R-03: breadcrumb removed. SECTION_LABELS map and getSectionLabel helper
   deleted with the JSX. Do NOT re-introduce; operator ruled remove-for-now
   over relocate-to-body. */

const AppBar = ({ title, children, showProjectSelector = true, projectSelectorProps = {} }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { query } = useAppSelector((state) => state.search);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { assetTypes } = useAppSelector((state) => state.asset);
  const { currentProject, projectList, loading: projectsLoading } = useAppSelector((state) => state.project);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Load projects if not already loaded and not currently loading
  useEffect(() => {
    if (activeCustomer?.id && projectList.length === 0 && !projectsLoading) {
      dispatch(fetchProjectsByCustomer(activeCustomer.id));
    }
  }, [activeCustomer?.id, projectList.length, projectsLoading, dispatch]);

  const typeMap = useMemo(() => {
    const m = {};
    for (const t of (assetTypes || [])) m[t.id] = t.name;
    return m;
  }, [assetTypes]);

  useEffect(() => {
    if (!activeCustomer?.id) return;
    api.getAssetsByCustomer(activeCustomer.id).then((r) => {
      const assets = (r.items || []).map(a => ({
        ...a,
        typeName: a.type?.name || typeMap[a.assetTypeId] || '',
      }));
      setAllAssets(assets);
    }).catch(() => {});
  }, [activeCustomer?.id, typeMap]);

  const fuse = useMemo(() => new Fuse(allAssets, FUSE_OPTIONS), [allAssets]);

  const handleMenuClick = () => {
    dispatch(setDrawerNavOpen(true));
  };

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    dispatch(setQuery(value));

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const results = fuse.search(value, { limit: 8 });
      setSuggestions(results.map(r => r.item));
      setShowSuggestions(results.length > 0);
    }, 300);
  }, [dispatch, fuse]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (query.trim()) {
      navigate('/search');
    }
  };

  const handleSuggestionClick = (asset) => {
    setShowSuggestions(false);
    dispatch(setQuery(''));
    navigate(`/asset/${asset.id}`);
  };

  const handleProjectNameClick = () => {
    setProjectPickerOpen(true);
  };

  const handleProjectSelect = (projectId) => {
    if (projectId) {
      navigate(`/projects/${projectId}`);
    }
  };

  return (
    <>
      <MuiAppBar position="static">
        <Toolbar sx={{ minHeight: { xs: 52 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleMenuClick}
            aria-label="Open navigation menu"
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Vertical divider after hamburger menu */}
          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: colorPalette.lightSteel }} />

          {/* R-03: breadcrumb removed per operator ruling (remove for now, do not
              relocate). Divider above still separates the hamburger from the
              project selector on the right. */}

          {/* Project name display (clickable) or custom selector */}
          {showProjectSelector && projectSelectorProps.ProjectSelectorComponent ? (
            projectSelectorProps.ProjectSelectorComponent
          ) : showProjectSelector && currentProject ? (
            <Box
              onClick={handleProjectNameClick}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: colorPalette.portlandCement,
                }}
              >
                {currentProject.name}
              </Typography>
            </Box>
          ) : showProjectSelector && !currentProject && projectList.length > 0 ? (
            <Box
              onClick={handleProjectNameClick}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: colorPalette.portlandCement,
                }}
              >
                Projects
              </Typography>
            </Box>
          ) : title ? (
            <Typography
              variant="body1"
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                color: colorPalette.portlandCement,
                px: 1,
              }}
            >
              {title}
            </Typography>
          ) : null}

          {children}

          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={MatracLogo}
              alt="Matrac by Assetrac — click to go home"
              onClick={() => navigate('/')}
              sx={{
                height: 18,
                width: 'auto',
                cursor: 'pointer',
              }}
            />
          </Box>

          {/* Search Bar with Type-Ahead */}
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{ width: 280, flexShrink: 0, display: 'flex', alignItems: 'center', position: 'relative', mb: 0 }}
              ref={searchRef}
            >
              <TextField
                size="small"
                placeholder="SEARCH ALL"
                value={query}
                onChange={handleSearchChange}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                fullWidth
                autoComplete="off"
                InputProps={{
                  sx: { fontSize: '0.875rem', height: 40 },
                }}
                sx={{
                  m: 0,  // No margin for global search in app bar
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  },
                }}
              />

              <Popper
                open={showSuggestions}
                anchorEl={searchRef.current}
                placement="bottom-end"
                style={{ zIndex: 1300, width: 400 }}
              >
                <Paper sx={{ mt: 1, maxHeight: 400, overflow: 'auto', backgroundColor: colorPalette.backgroundColor, border: `1px solid ${colorPalette.lightSteel}` }}>
                  <List dense disablePadding>
                    {suggestions.map((asset) => (
                      <ListItem key={asset.id} disablePadding>
                        <ListItemButton onClick={() => handleSuggestionClick(asset)} sx={{ py: 1 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {asset.name || asset.unitNumber || 'Unnamed'}
                                </Typography>
                                {asset.typeName && (
                                  <Chip label={asset.typeName} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                UUID: {asset.uuid || '-'} {asset.description ? `| ${asset.description}` : ''}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    <ListItem disablePadding>
                      <ListItemButton onClick={handleSearchSubmit} sx={{ py: 1, justifyContent: 'center' }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          View all results →
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
        </Toolbar>
      </MuiAppBar>

      {/* Project Picker Dialog */}
      <ProjectPickerDialog
        open={projectPickerOpen}
        onClose={() => setProjectPickerOpen(false)}
        projects={projectList}
        onProjectSelect={handleProjectSelect}
        currentProjectId={currentProject?.id}
      />
    </>
  );
};

export default AppBar;
